import http from "@services/httpService"

/**
 * Dashboard CS / Hậu mãi (Feature-0197). Backend: `CsDashboardAppService`
 * (`api/services/app/CsDashboard/*`). Mọi method là read-only.
 */

/** Widget W-CS-01 — 6 hàng đợi việc của người dùng hiện tại. */
export interface ICsMyTasks {
  /** Batch thông báo phí đang chờ NGƯỜI gửi (NEW/READYTOSEND) — theo phạm vi dự án. */
  feeNotificationDraftWaiting: number
  myOpenFeedback: number
  /** Tập con của myOpenFeedback đã dùng ≥ 80% thời hạn xử lý. */
  myFeedbackNearSla: number
  myHandoverToday: number
  pendingCustomerChangeRequests: number
  failedFeeNotificationBatches: number
}

/** Widget W-CS-02 — một dòng cho mỗi loại phí, chia 3 khung cảnh báo. */
export interface IFeeDueByType {
  feeTypeId: number
  feeTypeCode?: string
  /** Tên lấy từ bảng dịch; rỗng thì FE hiển thị `feeTypeCode`. */
  feeTypeName?: string
  level1Days: number
  level2Days: number
  level3Days: number
  level1Amount: number
  level1Count: number
  level2Amount: number
  level2Count: number
  level3Amount: number
  level3Count: number
  totalAmount: number
  totalCount: number
}

export interface IFeeDueSummary {
  byFeeType: IFeeDueByType[]
  totalLevel1Amount: number
  totalLevel1Count: number
  /** Chưa thu đã đến hạn trong kỳ trước — KHÔNG phải "khung đỏ của kỳ trước". */
  previousPeriodUnpaidAmount?: number | null
  maxWindowDays: number
}

/** Widget W-CS-04 — một ô của bảng trạng thái × loại thông báo. */
export interface IFeeBatchByStatus {
  status: number
  feeNotificationType: number
  batchCount: number
}

export interface IFeeBatchSummary {
  byStatus: IFeeBatchByStatus[]
  totalBatchCount: number
  /** null = kỳ này chưa gửi lần nào → hiển thị "—", KHÔNG phải 0%. */
  emailSuccessRate?: number | null
  smsSuccessRate?: number | null
  failedContractCount: number
}

/** Widget W-CS-03 — một điểm trên đường xu hướng 6 tháng. */
export interface IFeeAgingTrendPoint {
  pointDate: string
  overDue30: number
  overDue60: number
  overDue90: number
  overDueMoreThan90: number
  totalOverdue: number
  /** true = điểm này bị cắt theo trần hợp đồng nên thiếu số. */
  isTruncated: boolean
}

export interface IFeeOverdueAging {
  calculateDate: string
  overDue30: number
  overDue60: number
  overDue90: number
  overDueMoreThan90: number
  totalOverdue: number
  contractCount: number
  /**
   * true = vượt trần hợp đồng nên số liệu THIẾU. Widget bắt buộc cảnh báo, không được hiển thị như
   * tổng đầy đủ.
   */
  isTruncated: boolean
  /** 6 điểm cuối tháng, cũ → mới; điểm cuối là mốc hiện tại. */
  trend: IFeeAgingTrendPoint[]
}

/** Widget W-CS-05 — hiệu quả nhắc nợ, đo trên HỢP ĐỒNG (nhắc nhiều lượt vẫn tính một). */
export interface IReminderEffectiveness {
  sentCount: number
  paidWithinWindowCount: number
  /** null = kỳ chưa gửi lượt nhắc nào → hiển thị "—", KHÔNG phải 0%. */
  effectivenessRate?: number | null
  /** Cửa sổ ghi nhận (ngày), lấy từ setting. */
  windowDays: number
}

/** Widget W-CS-06 — một đợt nộp hồ sơ sổ hồng. */
export interface IPinkBookPhaseBucket {
  phaseId: number
  phaseCode?: string
  phaseName?: string
  contractCount: number
  avgAgeInPhaseDays?: number | null
  isDelayed: boolean
  submissionDueDate?: string | null
  submissionDateToTNVMT?: string | null
  expectedDate?: string | null
}

/** Widget W-CS-06 — một trạng thái hồ sơ. */
export interface IPinkBookStatusBucket {
  statusId?: number | null
  statusName?: string | null
  contractCount: number
  /** null = nhóm có hồ sơ thiếu lịch sử trạng thái ⇒ hiển thị "—", không phải 0 ngày. */
  avgAgeInStatusDays?: number | null
  isHistoryIncomplete: boolean
  issuedCount: number
}

export interface IPinkBookPipeline {
  byPhase: IPinkBookPhaseBucket[]
  byStatus: IPinkBookStatusBucket[]
  totalContractCount: number
  issuedContractCount: number
  isStatusHistoryIncomplete: boolean
}

/** Widget W-CS-07 — phí sổ hồng, đọc từ công nợ hai loại phí RGF/ADF. */
export interface IPinkBookFeeSummary {
  totalContracts: number
  paidCount: number
  unpaidCount: number
  unpaidAmount: number
  overdueUnpaidCount: number
  overdueUnpaidAmount: number
  /** Hợp đồng đã lập phí nhưng CHƯA có dòng công nợ — "chưa ghi công nợ", khác "chưa thu". */
  missingLedgerCount: number
}

/** Widget W-CS-08 — một căn quá hạn bàn giao. */
export interface IHandoverOverdueUnit {
  unitId: number
  unitCode?: string
  projectName?: string
  planToDate: string
  overdueDays: number
}

export interface IHandoverOnTime {
  /** null = kỳ chưa có lượt bàn giao nào có kết cục → hiển thị "—". */
  onTimeRate?: number | null
  completedCount: number
  onTimeCount: number
  failedCount: number
  overdueUnitCount: number
  overdueUnits: IHandoverOverdueUnit[]
}

/** Widget W-CS-09 — công suất slot của một ngày. */
export interface IHandoverDayCapacity {
  date: string
  bookedCount: number
  capacity: number
  /** null = ngày không mở lịch → hiển thị "—", KHÔNG phải 0%. */
  utilizationRate?: number | null
}

/** Widget W-CS-10 — vi phạm theo cấp escalation. */
export interface IFeedbackViolationLevel {
  levelNumber: number
  violationCount: number
}

/** Widget W-CS-10 — một tuần trên đường xu hướng. */
export interface IFeedbackWeeklyPoint {
  weekStart: string
  openedCount: number
  closedCount: number
}

export interface IFeedbackSlaSummary {
  openedCount: number
  closedCount: number
  backlogCount: number
  /** null = không đo được ticket nào → hiển thị "—". */
  firstResponseSlaRate?: number | null
  resolutionSlaRate?: number | null
  /** Ticket không khớp cấu hình SLA nào. */
  noSlaCount: number
  /** Ticket thuộc dự án chưa khai giờ làm việc. */
  noBusinessHoursCount: number
  violationsByLevel: IFeedbackViolationLevel[]
  pendingEscalations: number
  weeklyTrend: IFeedbackWeeklyPoint[]
}

/** Widget W-CS-11 - mot phieu thay doi thong tin dang cho duyet. */
export interface ICustomerChangePending {
  requestId: number
  customerId: number
  customerName?: string
  customerCode?: string
  creationTime: string
  waitingDays: number
}

export interface ICustomerChangeSummary {
  /** Toan pham vi du an - KHAC counter "cho toi duyet" cua widget Viec cua toi. */
  pendingCount: number
  approvedCount: number
  rejectedCount: number
  /** null = ky chua chot phieu nao -> hien thi "—". */
  avgApprovalDays?: number | null
  oldestPending: ICustomerChangePending[]
}

/** Widget W-CS-12 - khach hang moi theo du an. */
export interface ICustomerCountByProject {
  projectId: number
  projectName?: string
  customerCount: number
}

/** Widget W-CS-12 - khach hang moi theo nhom; groupId null = chua gan nhom. */
export interface ICustomerCountByGroup {
  groupId?: number | null
  groupName?: string | null
  customerCount: number
}

export interface INewCustomerSummary {
  newCount: number
  /** null = widget khong bat so ky truoc. */
  previousNewCount?: number | null
  /** Khach hang moi chua gan hop dong nen khong quy duoc ve du an. */
  withoutProjectCount: number
  byProject: ICustomerCountByProject[]
  byGroup: ICustomerCountByGroup[]
}

/** Widget W-CS-13 - workload cua mot nhan vien; staffId null = viec chua phan cong. */
export interface IStaffWorkload {
  staffId?: number | null
  staffName?: string | null
  openFeedbackCount: number
  nearSlaCount: number
  handoverUpcomingCount: number
  pendingChangeRequestCount: number
  totalOpen: number
  closedInPeriod: number
  isOverloaded: boolean
}

/** Widget W-CS-14 - mot dong cua bang "viec cua team". */
export interface ICsMyTasksPerStaff {
  staffId: number
  staffName?: string | null
  /** Nguyen 6 counter cua W-CS-01 cho nhan vien nay (cung nguon so). */
  tasks: ICsMyTasks
  /** Tong 4 counter THEO NGUOI (2 counter con lai la cap du an). */
  totalPersonalTasks: number
}

class DashboardCsService {
  public async getMyTasks(): Promise<ICsMyTasks> {
    const res = await http.get("api/services/app/CsDashboard/GetMyTasks", {})
    return res.data.result
  }

  public async getFeeDueSummary(
    params?: Record<string, unknown>
  ): Promise<IFeeDueSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetFeeDueSummary",
      { params }
    )
    return res.data.result
  }

  public async getFeeNotificationBatchSummary(
    params?: Record<string, unknown>
  ): Promise<IFeeBatchSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetFeeNotificationBatchSummary",
      { params }
    )
    return res.data.result
  }

  public async getFeeOverdueAging(
    params?: Record<string, unknown>
  ): Promise<IFeeOverdueAging> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetFeeOverdueAging",
      { params }
    )
    return res.data.result
  }

  public async getReminderEffectiveness(
    params?: Record<string, unknown>
  ): Promise<IReminderEffectiveness> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetReminderEffectiveness",
      { params }
    )
    return res.data.result
  }

  public async getPinkBookPipeline(
    params?: Record<string, unknown>
  ): Promise<IPinkBookPipeline> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetPinkBookPipeline",
      { params }
    )
    return res.data.result
  }

  public async getPinkBookFeeSummary(
    params?: Record<string, unknown>
  ): Promise<IPinkBookFeeSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetPinkBookFeeSummary",
      { params }
    )
    return res.data.result
  }

  public async getHandoverOnTimeRate(
    params?: Record<string, unknown>
  ): Promise<IHandoverOnTime> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetHandoverOnTimeRate",
      { params }
    )
    return res.data.result
  }

  public async getHandoverWeeklyCapacity(
    params?: Record<string, unknown>
  ): Promise<IHandoverDayCapacity[]> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetHandoverWeeklyCapacity",
      { params }
    )
    return res.data.result
  }

  public async getFeedbackSlaSummary(
    params?: Record<string, unknown>
  ): Promise<IFeedbackSlaSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetFeedbackSlaSummary",
      { params }
    )
    return res.data.result
  }

  public async getCustomerChangeRequestSummary(
    params?: Record<string, unknown>
  ): Promise<ICustomerChangeSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetCustomerChangeRequestSummary",
      { params }
    )
    return res.data.result
  }

  public async getNewCustomerSummary(
    params?: Record<string, unknown>
  ): Promise<INewCustomerSummary> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetNewCustomerSummary",
      { params }
    )
    return res.data.result
  }

  public async getTeamWorkload(
    params?: Record<string, unknown>
  ): Promise<IStaffWorkload[]> {
    const res = await http.get(
      "api/services/app/CsDashboard/GetTeamWorkload",
      { params }
    )
    return res.data.result
  }

  public async getTeamTasks(
    params?: Record<string, unknown>
  ): Promise<ICsMyTasksPerStaff[]> {
    const res = await http.get("api/services/app/CsDashboard/GetTeamTasks", {
      params,
    })
    return res.data.result
  }
}

export default new DashboardCsService()
